/**
 * Which credential types each use takes: the ones the gateway logs in with for
 * it, so a picker never offers a credential the gateway would refuse or ignore.
 *
 * From the gateway's own matches (2026-10-06): HTTP, REST and eAPI log in with a
 * username and password only (`http_pool_factory`, `config_manager`); SSH and
 * NETCONF with a password or a key; SNMP with a v2c community or a v3 user; a
 * device behind a console port is auto-logged-in with a password only; an SSH
 * tunnel's bastion is an SSH login. Nothing uses an X.509 certificate yet, and
 * an API key only signs a controller in (`controllerTypeInfo` lists those).
 *
 * Split out from `CredentialSelect` so components can import these without
 * tripping fast refresh.
 */
import type { Credential } from '@/hooks/useCredentials';

type CredType = Credential['type'];

/** SNMP polling and writes. */
export const SNMP_CREDENTIAL_TYPES: readonly CredType[] = ['snmpV2Community', 'snmpV3'];

/** An SSH login: SSH, NETCONF, a console server, an SSH tunnel's bastion. */
export const SSH_CREDENTIAL_TYPES: readonly CredType[] = ['usernamePassword', 'sshKey'];

/** A password-only login: HTTP(S), REST, eAPI, Telnet, the device behind a console port. */
export const PASSWORD_CREDENTIAL_TYPES: readonly CredType[] = ['usernamePassword'];

/**
 * The types a connection takes, by its session type (`ssh`, `telnet`, `http`,
 * `https`, `console`, `snmp`, `netconf`) or service (`rest`, `eapi`, …).
 */
export function credentialTypesFor(sessionOrService: string): readonly CredType[] {
  switch (sessionOrService.toLowerCase()) {
    case 'snmp':
      return SNMP_CREDENTIAL_TYPES;
    case 'ssh':
    case 'netconf':
      return SSH_CREDENTIAL_TYPES;
    default:
      return PASSWORD_CREDENTIAL_TYPES;
  }
}
